import { LoggingService } from '@logging/logging.service';
import { Component, Output, Input, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { MsalModule, MsalService, MSAL_INSTANCE } from '@azure/msal-angular';
import { IPublicClientApplication, PublicClientApplication, Configuration } from '@azure/msal-browser';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@design-system/buttons/button/button.component';
import { AuthenticationService } from '@auth/authentication.service';
import { ActiveSecurityContextStateService } from '@auth/active-security-context.state-service';
import { LogType } from '@logging/log';
import { environment } from '@env/environment';
import { OnScreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';

// MSAL instance factory for standalone component
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '', // This will be populated from environment settings
      authority: '', // This will be populated from environment settings
      redirectUri: '', // This will be populated from environment settings
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false
    }
  });
}

//TODO Refactor this to use the new Unity UI Framework
@Component({
  selector: 'app-microsoft-login',
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    MsalModule
  ],
  providers: [
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    MsalService
  ],
  templateUrl: './microsoft-login.component.html',
  styleUrls: ['./microsoft-login.component.scss']
})
export class MicrosoftloginComponent implements OnInit, OnDestroy {
  @Input() loggedIn: boolean | undefined;
  @Output() isLoggedInEvent = new EventEmitter<boolean>();

  private msalConfigured = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private authenticationService: AuthenticationService,
    private logging: LoggingService,
    private activeSecurityContextStateService: ActiveSecurityContextStateService,
    private onScreenMessagingService: OnScreenMessagingService
  ) {
  }

  async ngOnInit(): Promise<void> {
    try {
      // Wait for settings and configure MSAL
      if (environment?.msalSettings) {
        const msalConfig: Configuration = {
          auth: {
            clientId: environment.msalSettings.clientId,
            authority: environment.msalSettings.authority,
            redirectUri: environment.msalSettings.redirect,
            navigateToLoginRequestUrl: true
          },
          cache: {
            cacheLocation: 'sessionStorage',
            storeAuthStateInCookie: false
          }
        };
        
        // Create a new instance with the complete configuration
        const msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();
        
        // Replace the existing instance
        Object.assign(this.authService.instance, msalInstance);
        this.msalConfigured = true;

        // Now handle any redirect response
        await this.handleRedirect();
      } else {
        this.logging.error("MSAL settings not found in app configuration", { type: LogType.Error });
        this.handleAuthenticationError("MSAL settings not found in app configuration");
      }
    } catch {
      this.logging.error("Error initializing MSAL", { type: LogType.Error });
      this.handleAuthenticationError();
    }
  }

  private async handleRedirect(): Promise<void> {
    try {
      const result = await this.authService.handleRedirectObservable().toPromise();
      if (result) {
        this.logging.info("Authentication successful:", result);
        this.authService.instance.setActiveAccount(result.account);
        this.authenticateWithToken(result);
      } else {
        this.logging.info("No redirect result. Login required.");
        this.loggedIn = false;
      }
    } catch {
      this.logging.error("Error in handleRedirectObservable", { type: LogType.Error });
      this.handleAuthenticationError();
    }
  }
  
  async login(): Promise<void> {

    if (!this.msalConfigured) {
      this.logging.error("MSAL not configured yet", { type: LogType.Error });
      return;
    }

    try {
      await this.authService.loginRedirect();
    } catch(error: any) {
      this.logging.error("Error during login redirect", { type: LogType.Error });
      this.handleAuthenticationError(error?.error?.message ?? error?.message);
    }
  }

  /**
   * Authenticates with the Microsoft token from the MSAL result
   * @param result The MSAL authentication result
   */
  private authenticateWithToken(result: any): void {
    const idToken = result?.idToken;
    if (!idToken) {
      this.logging.error("No idToken found in MSAL result");
      this.handleAuthenticationError();
      return;
    }

    this.logging.info("Authenticating with Microsoft token");
    this.authenticationService.userSignInByToken$(idToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (authState) => {
          if (authState.success) {
            this.loggedIn = true;
            this.activeSecurityContextStateService.redirectToBestRoute();
          } else {
            this.handleAuthenticationError("Authentication failed, redirecting to login");
          }
        },
        error: (error: any) => {
          this.logging.error("Error during Microsoft authentication", error);
          this.handleAuthenticationError(error?.error?.message ?? error?.message);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handles authentication errors by show
   */
  private handleAuthenticationError(errorMessage?: string): void {
    this.loggedIn = false;

    if(errorMessage) 
      this.onScreenMessagingService.warn(errorMessage);
  
    this.authenticationService.signOut();
  }
}
